import React, { useState, useEffect, RefObject } from 'react';
import { Save, Check, CircleMinus, CircleCheck } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import {
    Agency,
    ProcedureTemplate,
    ProcedureTemplateCreatePayload,
    ProcedureType,
} from '../../services/auditService';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

interface ProcedureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ProcedureTemplateCreatePayload, id?: number) => Promise<void> | void;
    initialData: ProcedureTemplate | null;
    agencies?: Agency[];
    /** Non-empty ⇒ the caller is an audit lead: new procedures are forced to
     * Agency-Private and the owning agency is restricted to these agencies (the
     * ones the lead heads). One agency ⇒ auto-selected, no picker. Empty/undefined
     * ⇒ normal GRC authoring. */
    leadAgencies?: { id: number; name: string }[];
    onToggleActive?: (id: number, next: 0 | 1) => Promise<void>;
    embedded?: boolean;
    disabled?: boolean;
    hideEmbeddedFooter?: boolean;
    embeddedFormRef?: RefObject<HTMLFormElement | null>;
}

// Module-owned types kept here as label fallbacks only — read-only on
// existing rows that were auto-created from their owning module (Risk, Control,
// or SOP master). They are filtered out of the creatable dropdown below, because
// Audit Procedures is the single source of truth and those rows register from
// their owning module.
const TYPE_OPTIONS: { value: ProcedureType; label: string }[] = [
    { value: 'Checklist',             label: 'Checklist' },
    { value: 'Questionnaire',         label: 'Questionnaire' },
    { value: 'Form',                  label: 'Form' },
    { value: 'Rating',                label: 'Rating System' },
    { value: 'Confirmation',          label: 'Balance Confirmation' },
    { value: 'PhysicalCheck',         label: 'Physical Inspection' },
    { value: 'Walkthrough',           label: 'Process Walkthrough (legacy)' },
    { value: 'WalkthroughDesign',     label: 'Process Walkthrough — Test of Design' },
    { value: 'WalkthroughCompliance', label: 'Process Walkthrough — Test of Compliance' },
    { value: 'RiskAssessment',        label: 'Risk Assessment' },
    { value: 'ControlTest',           label: 'Control Test' },
];

const MODULE_OWNED_TYPES: ProcedureType[] = [
    'RiskAssessment', 'ControlTest',
    'WalkthroughDesign', 'WalkthroughCompliance', 'Walkthrough',
];

const TYPE_SELECT_OPTIONS = TYPE_OPTIONS
    .filter(t => !MODULE_OWNED_TYPES.includes(t.value))
    .map(t => ({ value: t.value, label: t.label }));

const blank: ProcedureTemplateCreatePayload = {
    procedure_name: '',
    procedure_description: '',
    ownership_scope: 'GRC-Common',
    owner_agency_id: null,
    procedure_type: 'Checklist',
    effective_start_date: null,
    evidence_mandatory: false,
    evidence_kinds: null,
    linked_sop_id: null,
    linked_risk_assessment_template_id: null,
    linked_control_test_template_id: null,
    is_active: 1,
    is_approved: 0,
};

const ProcedureModal: React.FC<ProcedureModalProps> = ({
    isOpen, onClose, onSave, initialData, agencies, leadAgencies, onToggleActive,
    embedded, disabled = false, hideEmbeddedFooter = false, embeddedFormRef,
}) => {
    // Audit-lead mode — Agency-Private, owning agency restricted to the lead's
    // own agencies (auto-picked when there is only one).
    const leadMode = (leadAgencies?.length ?? 0) > 0;
    const singleLeadAgency = leadAgencies?.length === 1;
    const fieldVh = useFieldValidation();
    const { allowed: canModify, loading: permLoading } = usePermission('ADT_PROCEDURES', 'view+modify');
    // Audit-lead authoring is authorized POSITIONALLY (agency head) — an external
    // audit lead holds no GRC ADT_PROCEDURES grant. In lead mode the GRC RBAC gate
    // must NOT disable Submit; the backend enforces agency-head authorization.
    const authorized = leadMode || canModify;
    const gateLoading = leadMode ? false : permLoading;
    const [formData, setFormData] = useState<ProcedureTemplateCreatePayload>(blank);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        if (initialData) {
            const { id, created_on, modified_on, modified_by, current_version, group_id, is_active, is_approved, ...rest } = initialData as any;
            setFormData({ ...blank, ...rest });
        } else if (leadMode) {
            // Audit lead — force Agency-Private; default to the first headed agency.
            setFormData({ ...blank, ownership_scope: 'Agency-Private', owner_agency_id: leadAgencies![0].id });
        } else {
            setFormData(blank);
        }
        setSubmitting(false);
        setToggling(false);
        fieldVh.reset();
    }, [initialData, isOpen, leadMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = <K extends keyof ProcedureTemplateCreatePayload>(k: K, v: ProcedureTemplateCreatePayload[K]) =>
        setFormData(prev => ({ ...prev, [k]: v }));

    const isAgencyPrivate = formData.ownership_scope === 'Agency-Private';
    // Only approved + active agencies belong in the picker — the shared agencies
    // list carries draft/submitted/rejected rows too.
    const agencyOptions = (agencies ?? [])
        .filter(a => (a.is_active ?? 1) === 1 && a.workflow_state === 'approved')
        .map(a => ({ value: String(a.id), label: a.audit_agency_name }));

    // BRD#855: filter type options when Agency-Private scope is selected.
    // Also: RiskAssessment / ControlTest are not user-creatable here (mirrored from
    // Risk / Control modules). When editing a row that already has one of those types,
    // show its current label so the dropdown renders correctly, but it stays disabled.
    const isModuleOwnedRow = !!initialData && MODULE_OWNED_TYPES.includes(formData.procedure_type);
    let availableTypeOptions = TYPE_SELECT_OPTIONS;
    if (isModuleOwnedRow) {
        const current = TYPE_OPTIONS.find(t => t.value === formData.procedure_type);
        if (current) availableTypeOptions = [{ value: current.value, label: current.label }];
    }

    const canSave = !!(formData.procedure_name.trim() && formData.procedure_type);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!authorized) return;
        const engineOk = fieldVh.validateAll([
            { name: 'procedure_name', code: 109, value: formData.procedure_name, required: true },
            { name: 'procedure_description', code: 110, value: formData.procedure_description },
        ]);
        if (!engineOk) return;
        if (!formData.procedure_type) return;
        setSubmitting(true);
        try {
            if (initialData) {
                // Strip is_active from the update payload — toggle owns it.
                const { is_active, is_approved, ...rest } = formData;
                await Promise.resolve(onSave(rest as ProcedureTemplateCreatePayload, initialData.id));
            } else {
                await Promise.resolve(onSave(formData));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async () => {
        if (!initialData || !onToggleActive) return;
        const next: 0 | 1 = initialData.is_active ? 0 : 1;
        setToggling(true);
        try {
            await onToggleActive(initialData.id, next);
        } finally {
            setToggling(false);
        }
    };

    const isEdit = !!initialData;
    const isActive = !!initialData?.is_active;
    const typeLabel = TYPE_OPTIONS.find(t => t.value === formData.procedure_type)?.label ?? formData.procedure_type;

    const formFields = (
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {/* System code (PRC-N) shown in the list — read-only, prefilled.
                Only on edit; spans the full row so Type/Name stay paired below. */}
            {isEdit && (
                <div className="col-span-2">
                    <FormControl.Input
                        type="text"
                        label="Code"
                        value={(initialData as any)?.code || '—'}
                        disabled
                        data-testid="INP_procedure_code"
                    />
                </div>
            )}
            {/* Procedure Type — always read-only on edit (type is fixed after creation) or when embedded;
                editable select only on create. */}
            {isEdit || embedded ? (
                <FormControl.Input
                    type="text"
                    label="Procedure Type"
                    value={typeLabel}
                    disabled={true}
                    data-testid="SEL_procedure_type"
                />
            ) : (
                <div>
                    <FormControl.SearchSelect
                        label="Procedure Type"
                        required
                        options={availableTypeOptions}
                        value={formData.procedure_type}
                        onChange={v => set('procedure_type', v ? String(v) as ProcedureType : 'Checklist')}
                        disabled={disabled}
                        data-testid="SEL_procedure_type"
                    />
                </div>
            )}
            <ValidatedInput
                vh={fieldVh}
                name="procedure_name"
                code={109}
                required
                label="Procedure Name"
                value={formData.procedure_name}
                onChange={e => set('procedure_name', e.target.value)}
                placeholder="e.g. IT Access Review"
                disabled={disabled}
                data-testid="INP_procedure_name"
            />
            {/* BRD#854: Agency selector — only when Agency-Private.
                No empty placeholder when GRC-Common so the following fields
                flow naturally two-per-row (no gap beside Procedure Type). */}
            {isAgencyPrivate && (
                leadMode ? (
                    singleLeadAgency ? (
                        // Audit lead with one agency — fixed, shown read-only.
                        <FormControl.Input
                            type="text"
                            label="Owner Agency"
                            value={leadAgencies![0].name}
                            disabled
                            data-testid="INP_procedure_owner_agency"
                        />
                    ) : (
                        // Audit lead with several agencies — restrict the picker to theirs.
                        <FormControl.SearchSelect
                            label="Owner Agency"
                            required
                            options={leadAgencies!.map(a => ({ value: String(a.id), label: a.name }))}
                            value={formData.owner_agency_id ? String(formData.owner_agency_id) : ''}
                            onChange={v => set('owner_agency_id', v ? Number(v) : null)}
                            placeholder="Select your agency…"
                            disabled={disabled || isEdit}
                            data-testid="INP_procedure_owner_agency"
                        />
                    )
                ) : (
                    <FormControl.SearchSelect
                        label="Owner Agency"
                        required
                        options={agencyOptions}
                        value={formData.owner_agency_id ? String(formData.owner_agency_id) : ''}
                        onChange={v => set('owner_agency_id', v ? Number(v) : null)}
                        placeholder="Select owning agency…"
                        disabled={disabled}
                        data-testid="INP_procedure_description"
                    />
                )
            )}

            {/* Effective Start Date */}
            <ValidatedDatePicker
                vh={fieldVh}
                name="effective_start_date"
                code={305}
                label="Effective Start Date (Optional)"
                value={formData.effective_start_date || ''}
                onChange={e => set('effective_start_date', e.target.value || null)}
                disabled={disabled}
                data-testid="INP_procedure_effective_start_date"
            />

            {/* Row 3 — Description | Evidence Requirements */}
            <ValidatedTextarea
                vh={fieldVh}
                name="procedure_description"
                code={110}
                label="Description"
                value={formData.procedure_description || ''}
                onChange={e => set('procedure_description', e.target.value)}
                placeholder="What this procedure verifies, what evidence it expects."
                disabled={disabled}
                data-testid="INP_procedure_description"
            />
        </div>
    );

    if (embedded) {
        if (!isOpen) return null;
        return (
            <div>
                <form ref={embeddedFormRef} onSubmit={handleSubmit}>
                    {formFields}
                </form>
                {!hideEmbeddedFooter && (
                    <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
                        <Button
                            variant="update"
                            icon={Check}
                            onClick={handleSubmit as any}
                            disabled={gateLoading || !authorized || !canSave || submitting}
                            title={!authorized ? NOT_AUTHORISED_MSG : undefined}
                        >
                            {submitting ? 'Updating...' : 'Update'}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>
                {isEdit ? (
                    <>Editing "<span className="item-highlight">{initialData!.procedure_name}</span>"</>
                ) : (
                    'Create Procedure'
                )}
            </Modal.Header>
            <Modal.Body>
                <form id="procedure-form" onSubmit={handleSubmit} data-testid="MOD_procedure_form">
                    {formFields}
                </form>
            </Modal.Body>
            <Modal.Footer className={isEdit && onToggleActive ? 'app-modal-footer--split' : ''}>
                {isEdit && onToggleActive && (
                    <Button
                        variant={isActive ? 'inactive-outline' : 'active-outline'}
                        icon={isActive ? CircleMinus : CircleCheck}
                        onClick={handleToggleActive}
                        disabled={toggling || submitting}
                        data-testid="BTN_procedure_toggle_active"
                    >
                        {toggling ? '...' : isActive ? 'Inactive' : 'Active'}
                    </Button>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        type="submit"
                        form="procedure-form"
                        variant={isEdit ? 'update' : 'fill'}
                        icon={isEdit ? Check : Save}
                        disabled={gateLoading || !authorized || !canSave || submitting || toggling}
                        title={!authorized ? NOT_AUTHORISED_MSG : undefined}
                        data-testid="BTN_procedure_submit"
                    >
                        {submitting
                            ? (isEdit ? 'Updating...' : 'Submitting...')
                            : (isEdit ? 'Update' : 'Submit')}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ProcedureModal;
